import type {
  ProductCategory,
  ProductKind,
  ProductWeight,
} from '@iheartjane/types';

// Mirrors api/app/models/rules/validate/types.rb. These shapes are the canonical
// per-special-type rule schemas as accepted by the backend dry-schema. Keep
// these in sync with that file.

type KindRule = {
  brand_subtype?: string;
  kind: ProductKind;
  root_subtype?: string;
};

type WeightValueRule = {
  units: string;
  value: number;
};

type BulkWeightRule = {
  maximum_price?: number;
  minimum_price?: number;
  price_id: ProductWeight;
};

type BulkWeightValueRule = {
  maximum_price?: number;
  minimum_price?: number;
  weight_value: WeightValueRule;
};

export type ProductThreshold = {
  maximum_price?: number;
  minimum_price?: number;
};

type InclusionRule = {
  brand_ids?: number[];
  brands?: string[];
  kinds?: KindRule[];
  lineages?: ProductCategory[];
  product_ids?: number[] | null;
  weight_values?: WeightValueRule[];
  weights?: ProductWeight[];
};

type ExclusionRule = InclusionRule;

type BulkPricingInclusionRule = {
  brand_ids?: number[];
  brands?: string[];
  bulk_weight_values?: BulkWeightValueRule[];
  bulk_weights?: BulkWeightRule[];
  kinds?: KindRule[];
  lineages?: ProductCategory[];
  product_ids?: number[] | null;
};

type BulkPricingExclusionRule = BulkPricingInclusionRule;

export type RuleSet = {
  excludes?: ExclusionRule[];
  includes?: InclusionRule[];
  product_threshold?: ProductThreshold;
};

export type BulkPricingRuleSet = {
  excludes?: BulkPricingExclusionRule[];
  includes?: BulkPricingInclusionRule[];
  product_threshold?: ProductThreshold;
};

export type SpecialTypeWithRules =
  | 'product'
  | 'cart_total'
  | 'spending_threshold'
  | 'bundle'
  | 'bulk_pricing';

export type RuleSetFor<T extends SpecialTypeWithRules> =
  T extends 'bulk_pricing' ? BulkPricingRuleSet : RuleSet;

// Form-state input shape. parseRulesFromApplyTo accepts this loose shape and
// emits the canonical RuleSetFor<T>. List fields are nullable because the
// legacy io-ts SpecialRulesV2 schema permits null on each one.
export type FormInclusion = {
  brand_ids?: number[] | null;
  brands?: string[] | null;
  bulk_weights?: BulkWeightRule[] | null;
  kinds?: KindRule[] | null;
  lineages?: string[] | null;
  product_ids?: number[] | null;
  weight_values?: WeightValueRule[] | null;
  weights?: ProductWeight[] | null;
};

export type FormExclusion = FormInclusion;

type FormProductThreshold = {
  maximum_price?: number | null;
  minimum_price?: number | null;
};

export type FormRuleSet = {
  excludes?: FormExclusion[] | null;
  includes?: FormInclusion[] | null;
  product_threshold?: FormProductThreshold | null;
};

// Backend's InclusionRule and ExclusionRule have identical fields (likewise for
// the bulk-pricing variants), so a single tuple per ruleset is the source of
// truth for which keys parseRulesFromApplyTo allows.
export const RULE_KEYS = [
  'brands',
  'brand_ids',
  'kinds',
  'lineages',
  'product_ids',
  'weights',
  'weight_values',
] as const satisfies ReadonlyArray<keyof InclusionRule>;

export const BULK_PRICING_RULE_KEYS = [
  'brands',
  'brand_ids',
  'bulk_weights',
  'bulk_weight_values',
  'kinds',
  'lineages',
  'product_ids',
] as const satisfies ReadonlyArray<keyof BulkPricingInclusionRule>;
